import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import {
  EntityListSection,
  usePersistedFilter,
} from "@tennis-matches/ui-components";
import { buildTournamentHref } from "../../../utils/href.js";

const FILTERS = ["all", "active", "planned", "finished", "cancelled_aborted"];
const FILTER_STORAGE_KEY = "tt.clubchamp.hub.filter";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __asInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function __lifecycle(item) {
  const status = __asStr(item?.status).toLowerCase();
  if (status === "finished" || status === "finish" || status === "past") {
    return "finished";
  }
  if (status === "cancelled" || status === "canceled") return "cancelled";
  if (status === "aborted") return "aborted";
  if (
    status === "running" ||
    status === "active" ||
    status === "in_progress" ||
    status === "entry"
  ) {
    return "active";
  }
  return "planned";
}

function __filterBucket(item) {
  const life = __lifecycle(item);
  if (life === "active") return "active";
  if (life === "finished") return "finished";
  if (life === "cancelled" || life === "aborted") return "cancelled_aborted";
  return "planned";
}

function __formatWhen(startDate, startTime) {
  const date = __asStr(startDate);
  if (!date) return "";
  try {
    const iso = startTime ? `${date}T${__asStr(startTime)}` : date;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return date;
    if (startTime) {
      return new Intl.DateTimeFormat("de-DE", {
        weekday: "short",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    }
    return new Intl.DateTimeFormat("de-DE", {
      weekday: "short",
      day: "numeric",
      month: "long",
    }).format(d);
  } catch {
    return date;
  }
}

function __displayName(item, tr) {
  const name = __asStr(item?.name);
  if (name) return name;
  return tr("hub.clubmeisterschaft.tournaments.nameFallback.generic");
}

function __ctaKey(life) {
  if (life === "active") {
    return "hub.clubmeisterschaft.tournaments.cta.continue";
  }
  if (life === "finished") {
    return "hub.clubmeisterschaft.tournaments.cta.results";
  }
  return "hub.clubmeisterschaft.tournaments.cta.open";
}

function __cardHref(item, life) {
  const code = __asStr(item?.code);
  if (!code || life === "cancelled") return null;
  const status =
    life === "finished" || life === "aborted"
      ? "finished"
      : life === "planned"
        ? "planned"
        : "running";
  return buildTournamentHref({
    appKey: "clubmeisterschaft",
    code,
    intent: "open",
    kind: "clubmeisterschaft",
    status,
  });
}

/**
 * @param {{
 *   t: (k: string, p?: object) => string,
 *   items?: Array<object>,
 *   loading?: boolean,
 *   statsHref?: string,
 * }} props
 */
export function ClubChampTournamentsSection({
  t,
  items = [],
  loading = false,
  statsHref = "/hub/insights/clubmeisterschaft",
}) {
  const tr = typeof t === "function" ? t : (k) => k;
  const safeItems = Array.isArray(items) ? items : [];
  const { filter, filterReady, setFilter, setFilterReady, selectFilter } =
    usePersistedFilter(FILTERS, FILTER_STORAGE_KEY, "all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const scopedItems = useMemo(() => {
    return safeItems.filter((item) => {
      const archived = Boolean(__asStr(item?.archivedAt));
      return showArchived ? archived : !archived;
    });
  }, [safeItems, showArchived]);

  const searchedItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scopedItems;
    return scopedItems.filter((item) => {
      const name = __asStr(item?.name).toLowerCase();
      const code = __asStr(item?.code).toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [scopedItems, search]);

  const buckets = useMemo(() => {
    const next = {
      all: [],
      active: [],
      planned: [],
      finished: [],
      cancelled_aborted: [],
    };
    for (const item of searchedItems) {
      const bucket = __filterBucket(item);
      next.all.push(item);
      if (next[bucket]) next[bucket].push(item);
    }
    return next;
  }, [searchedItems]);

  useEffect(() => {
    if (filterReady || loading) return;
    const preferred =
      buckets.active.length > 0
        ? "active"
        : buckets.planned.length > 0
          ? "planned"
          : "all";
    setFilter(preferred);
    setFilterReady(true);
  }, [buckets, filterReady, loading, setFilter, setFilterReady]);

  const visible = filter === "all" ? buckets.all : buckets[filter] || [];

  return (
    <EntityListSection
      titleId="cc-hub-tournaments-title"
      title={tr("hub.clubmeisterschaft.tournaments.title")}
      headerAside={
        <Link to={statsHref} className="mm-hub__tournamentsStatsLink">
          {tr("actions.stats")}
        </Link>
      }
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder={tr(
        "hub.clubmeisterschaft.tournaments.search.placeholder",
      )}
      searchAriaLabel={tr("hub.clubmeisterschaft.tournaments.search.aria")}
      archiveToggle={{
        checked: showArchived,
        onChange: setShowArchived,
        label: tr("hub.clubmeisterschaft.tournaments.archiveToggle"),
      }}
      filters={FILTERS.map((value) => ({
        value,
        label: tr(`hub.clubmeisterschaft.tournaments.filter.${value}`),
        count: buckets[value]?.length || 0,
      }))}
      filterValue={filter}
      onFilterChange={selectFilter}
      filtersAriaLabel={tr("hub.clubmeisterschaft.tournaments.filter.aria")}
      loading={loading}
      loadingMessage={tr("common.loading")}
      empty={!visible.length}
      emptyMessage={
        showArchived
          ? tr("hub.clubmeisterschaft.tournaments.empty.archived")
          : tr(`hub.clubmeisterschaft.tournaments.empty.${filter}`)
      }
    >
      {visible.length ? (
        <ul className="mm-hub__tournamentList">
          {visible.map((item) => {
            const code = __asStr(item?.code);
            const name = __displayName(item, tr);
            const life = __lifecycle(item);
            const href = __cardHref(item, life);
            const players = __asInt(item?.playersCount, 0);
            const whenLabel = __formatWhen(item?.startDate, item?.startTime);
            const statusKey = `hub.clubmeisterschaft.active.status.${life}`;
            const ctaKey = __ctaKey(life);
            const archived = Boolean(__asStr(item?.archivedAt));

            return (
              <li
                key={code}
                className={`mm-hub__tournamentCard card${archived ? " is-archived" : ""}`}
              >
                <div className="mm-hub__tournamentCardInner">
                  <div className="mm-hub__tournamentMedia" aria-hidden="true">
                    <div className="mm-hub__tournamentImgFallback" />
                  </div>
                  <div className="mm-hub__tournamentBody">
                    <div className="mm-hub__tournamentTop">
                      <div className="mm-hub__tournamentHeadings">
                        <h3 className="mm-hub__tournamentName">{name}</h3>
                        {code ? (
                          <p className="mm-hub__tournamentCode muted">{code}</p>
                        ) : null}
                      </div>
                      <span
                        className="mm-hub__tournamentStatus"
                        data-status={life}
                      >
                        {tr(statusKey)}
                      </span>
                    </div>
                    <div className="mm-hub__tournamentMeta">
                      {whenLabel ? (
                        <span className="mm-hub__tournamentMetaItem">
                          {whenLabel}
                        </span>
                      ) : null}
                      {players > 0 ? (
                        <span className="mm-hub__tournamentMetaItem">
                          {tr("hub.clubmeisterschaft.tournaments.players", {
                            count: players,
                          })}
                        </span>
                      ) : null}
                    </div>
                    <div className="mm-hub__tournamentActions">
                      {href ? (
                        <Link
                          to={href}
                          className="btn btn--primary mm-hub__tournamentCta"
                        >
                          <span>{tr(ctaKey)}</span>
                          <ChevronRight
                            size={16}
                            strokeWidth={2.5}
                            aria-hidden="true"
                          />
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </EntityListSection>
  );
}
